import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { getApiBaseUrl } from '../config/apiBase';

/**
 * Lufa Deal structure (matches API response)
 */
export interface LufaDeal {
  id: string;
  name: string;
  nameFr: string;
  price: number;
  originalPrice?: number;
  discountPercent?: number;
  category: 'protein' | 'produce' | 'dairy' | 'bakery' | 'prepared' | 'other';
  unit: string;
  imageUrl?: string;
  supplier?: string;
  productUrl?: string;
}

/**
 * Deals state
 */
export interface DealsState {
  lufa: LufaDeal[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
  selectedDeals: string[]; // IDs of deals user has selected for meal generation
}

const initialState: DealsState = {
  lufa: [],
  isLoading: false,
  error: null,
  lastUpdated: null,
  selectedDeals: [],
};

const getApiUrl = (): string => getApiBaseUrl();

/**
 * Fetch Lufa deals from API
 */
export const fetchLufaDeals = createAsyncThunk(
  'deals/fetchLufa',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/deals/lufa`);

      if (!response.ok) {
        throw new Error(`Failed to fetch deals: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch Lufa deals');
      }

      return {
        deals: data.deals as LufaDeal[],
        scrapedAt: data.scrapedAt,
      };
    } catch (error: any) {
      console.error('Error fetching Lufa deals:', error);
      return rejectWithValue(error.message || 'Failed to fetch deals');
    }
  }
);

const dealsSlice = createSlice({
  name: 'deals',
  initialState,
  reducers: {
    /**
     * Toggle deal selection for meal generation
     */
    toggleDealSelection: (state, action: PayloadAction<string>) => {
      const dealId = action.payload;
      const index = state.selectedDeals.indexOf(dealId);

      if (index === -1) {
        state.selectedDeals.push(dealId);
      } else {
        state.selectedDeals.splice(index, 1);
      }
    },

    /**
     * Select all deals of a category
     */
    selectAllInCategory: (state, action: PayloadAction<LufaDeal['category']>) => {
      const category = action.payload;
      const categoryDealIds = state.lufa
        .filter(deal => deal.category === category)
        .map(deal => deal.id);

      // Add all that aren't already selected
      categoryDealIds.forEach(id => {
        if (!state.selectedDeals.includes(id)) {
          state.selectedDeals.push(id);
        }
      });
    },

    /**
     * Deselect all deals
     */
    clearSelection: (state) => {
      state.selectedDeals = [];
    },

    /**
     * Select all protein and produce deals
     */
    selectAllProteinsAndProduce: (state) => {
      state.selectedDeals = state.lufa
        .filter(deal => deal.category === 'protein' || deal.category === 'produce')
        .map(deal => deal.id);
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set deals manually (for testing or offline)
     */
    setDeals: (state, action: PayloadAction<LufaDeal[]>) => {
      state.lufa = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLufaDeals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLufaDeals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lufa = action.payload.deals;
        state.lastUpdated = action.payload.scrapedAt;
        state.error = null;

        // Auto-select all protein and produce deals
        state.selectedDeals = action.payload.deals
          .filter(deal => deal.category === 'protein' || deal.category === 'produce')
          .map(deal => deal.id);
      })
      .addCase(fetchLufaDeals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  toggleDealSelection,
  selectAllInCategory,
  clearSelection,
  selectAllProteinsAndProduce,
  clearError,
  setDeals,
} = dealsSlice.actions;

// Selectors
export const selectAllDeals = (state: { deals: DealsState }) => state.deals.lufa;
export const selectProteinDeals = (state: { deals: DealsState }) =>
  state.deals.lufa.filter(deal => deal.category === 'protein');
export const selectProduceDeals = (state: { deals: DealsState }) =>
  state.deals.lufa.filter(deal => deal.category === 'produce');
export const selectSelectedDeals = createSelector(
  [(state: { deals: DealsState }) => state.deals.lufa, (state: { deals: DealsState }) => state.deals.selectedDeals],
  (lufa, selectedDeals) => lufa.filter(deal => selectedDeals.includes(deal.id))
);
export const selectIsLoading = (state: { deals: DealsState }) => state.deals.isLoading;
export const selectError = (state: { deals: DealsState }) => state.deals.error;
export const selectLastUpdated = (state: { deals: DealsState }) => state.deals.lastUpdated;

export default dealsSlice.reducer;
